import { AuthenticationUser, useRefreshTokenMutation } from '@codezo/graphql';
import { Center, Loader, Stack, Text } from '@mantine/core';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { devError } from '../utils/devLog';
import { scheduleTokenRefresh } from '../utils/tokenScheduler';
import { storeUserRole } from '../utils/tokenStorage';

interface ReAuthenticateCallbackProps {
	setUser: (user: AuthenticationUser) => void;
	setAccessToken: (token: string) => void;
}

// Global flag to prevent multiple simultaneous re-auth attempts
let isReAuthenticating = false;

export function ReAuthenticateCallback({ setUser, setAccessToken }: ReAuthenticateCallbackProps) {
	const navigate = useNavigate();
	const hasRun = useRef(false);

	const hash = location.hash.substring(1);
	const prevPath = new URLSearchParams(hash).get('prevPath');

	const [refreshTokenMutation] = useRefreshTokenMutation({
		onError: (err) => {
			devError('Re-auth: refresh mutation failed', err);
			const publicAppUrl = import.meta.env.VITE_PUBLIC_APP_URL || '';
			window.location.replace(`${publicAppUrl}/auth/login?logout=true`);
		},
	});

	useEffect(() => {
		// Two-layer dedup: hasRun guards same-instance re-renders (effect re-fires
		// when deps change), isReAuthenticating guards remounts triggered by the
		// post-success setUser/navigate cycle.
		if (hasRun.current || isReAuthenticating) {
			return;
		}

		hasRun.current = true;
		isReAuthenticating = true;

		const attemptReAuthentication = async () => {
			try {
				const result = await refreshTokenMutation();

				if (result.data?.refreshToken) {
					const { user, token } = result.data.refreshToken;

					setUser(user as AuthenticationUser);
					setAccessToken(token);
					storeUserRole(user.role);
					scheduleTokenRefresh(token);

					navigate(prevPath || '/dashboard', { replace: true });
				} else {
					const publicAppUrl = import.meta.env.VITE_PUBLIC_APP_URL || '';
					window.location.replace(`${publicAppUrl}/auth/login?logout=true`);
				}
			} catch (err) {
				devError('Re-auth: unexpected exception', err);
				const publicAppUrl = import.meta.env.VITE_PUBLIC_APP_URL || '';
				window.location.replace(`${publicAppUrl}/auth/login?logout=true`);
			} finally {
				// Reset the flag so a future legitimate re-auth attempt isn't permanently blocked
				// (e.g. if the user logs out and back in within the same SPA lifecycle).
				isReAuthenticating = false;
			}
		};

		attemptReAuthentication();
	}, [refreshTokenMutation, setUser, setAccessToken, navigate, prevPath]);

	return (
		<Center h="100vh">
			<Stack align="center" gap="lg">
				<Loader size="lg" color="codezo" />
				<Stack align="center" gap="xs">
					<Text size="lg" fw={500}>
						Refreshing your session...
					</Text>
					<Text size="sm" c="dimmed">
						Please wait while we restore your access.
					</Text>
				</Stack>
			</Stack>
		</Center>
	);
}
